import * as React from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { X } from 'lucide-react';

import { deduplicate } from '@klareospace/pharos-lib-utils';
import { Badge, cn, Input } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TagsInputHandle {
  /** Folds any text still sitting in the input into the tags list and returns the resulting array. */
  commitPendingTag: () => string[];
}

export interface TagsInputProps {
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  onChange: (tags: string[]) => void;
  placeholder: string;
  /** Accessible label for a chip's remove button. */
  removeTagAriaLabel: (tag: string) => string;
  value: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Creatable tag chip input — Enter or comma commits the current text as one or more tags (text is
 * split on commas, so a pasted "Fragile, Cold chain" yields two tags), blur folds any still-pending
 * text into the list, and duplicates are silently ignored. Exposes `commitPendingTag` via ref for
 * consumers with an external confirm action (e.g. an inline editor's Confirm button) that also
 * needs to fold text the user typed but never pressed Enter for.
 */
export const TagsInput = forwardRef<TagsInputHandle, TagsInputProps>(function TagsInput(
  { ariaLabel, className, disabled, onChange, placeholder, removeTagAriaLabel, value },
  ref,
): React.JSX.Element {
  const [inputValue, setInputValue] = useState('');

  /** Splits `raw` on commas into trimmed, non-empty, de-duplicated tags and merges them into `value`. */
  function commitText(raw: string): string[] {
    const candidates = raw
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (candidates.length === 0) return value;
    const next = deduplicate([...value, ...candidates]);
    if (next.length === value.length) return value;
    onChange(next);
    return next;
  }

  useImperativeHandle(ref, () => ({
    commitPendingTag: (): string[] => {
      const next = commitText(inputValue);
      setInputValue('');
      return next;
    },
  }));

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter' && event.key !== ',') return;
    event.preventDefault();
    commitText(inputValue);
    setInputValue('');
  }

  function handleBlur(): void {
    commitText(inputValue);
    setInputValue('');
  }

  function handleRemove(tag: string): void {
    onChange(value.filter((existing) => existing !== tag));
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Input
        aria-label={ariaLabel}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={(event): void => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        value={inputValue}
      />
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {value.map((tag) => (
            <Badge
              className="gap-1 rounded-md border-primary/30 bg-primary/5 px-2 font-medium text-primary"
              key={tag}
              variant="outline"
            >
              {tag}
              <button
                aria-label={removeTagAriaLabel(tag)}
                className="rounded-full p-0.5 hover:bg-primary/10"
                onClick={(): void => handleRemove(tag)}
                type="button"
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
});
